import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    OnInit,
} from '@angular/core';
import {
    AbstractControl,
    UntypedFormBuilder,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Flow, UpdateFlow } from '@myrtls/api-interfaces';

function jsonValidator(control: AbstractControl): ValidationErrors | null {
    try {
        JSON.parse(control.value);
    } catch {
        return { invalidJson: true };
    }
    return null;
}

@Component({
    selector: 'myrtls-create-flow-modal',
    template: `
        <h1 mat-dialog-title>{{ editMode ? 'Edit' : 'Create' }} Flow</h1>
        <div [formGroup]="createFlowForm" mat-dialog-content>
            <mat-form-field>
                <mat-label>Name</mat-label>
                <input matInput formControlName="name" />
            </mat-form-field>
            <mat-form-field class="mt-2">
                <mat-label>JSON</mat-label>
                <textarea matInput formControlName="json" rows="10"></textarea>
                @if (createFlowForm.controls.json.hasError('invalidJson')) {
                    <mat-error> Invalid JSON </mat-error>
                }
            </mat-form-field>
        </div>
        <div mat-dialog-actions class="flex items-center justify-end">
            <button mat-button (click)="dialogRef.close()">Cancel</button>
            <button
                mat-button
                [disabled]="!createFlowForm.valid"
                (click)="saveFlow()"
            >
                {{ editMode ? 'Update' : 'Create' }}
            </button>
        </div>
    `,
    styles: [
        `
            mat-form-field {
                width: 100%;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CreateFlowModalComponent implements OnInit {
    editMode = false;
    createFlowForm = this.fb.group({
        name: ['', [Validators.required]],
        json: ['', [Validators.required, jsonValidator]],
    });

    constructor(
        private readonly fb: UntypedFormBuilder,
        public dialogRef: MatDialogRef<UpdateFlow>,
        @Inject(MAT_DIALOG_DATA) public data: { flow?: Flow },
    ) {}

    ngOnInit(): void {
        if (this.data?.flow) {
            this.editMode = true;
            this.createFlowForm.patchValue({
                name: this.data.flow.name,
                json: JSON.stringify(this.data.flow.json, null, 2),
            });
        }
    }

    saveFlow(): void {
        if (!this.createFlowForm.valid) {
            this.createFlowForm.markAsTouched();
            return;
        }

        const { name, json } = this.createFlowForm.value;

        const flowPayload: Partial<Flow> = {
            name,
        };

        if (!this.editMode || this.createFlowForm.controls.json.dirty) {
            flowPayload.json = JSON.parse(json);
        }

        this.dialogRef.close(flowPayload);
    }
}
